import { useEffect } from 'react';

const SELECTOR = '[data-reveal]:not([data-revealed]):not([data-reveal-pending])';

// Fades [data-reveal] elements inside `ref` in the first time they scroll into view.
//
// Content is visible by default: only elements that start below the screen are
// marked `data-reveal-pending` (hidden by CSS) while they wait, then switched to
// `data-revealed` (which plays the entrance). Anything already on screen, or any
// browser where this can't run, simply shows the content. On cleanup every pending
// element is released, so nothing can get stuck hidden.
//
// Attributes are used instead of classes because React rewrites `className`
// whenever a component's classes change. Elements added later (re-mounted
// components, hot reloads) are picked up by a MutationObserver.
const useScrollReveal = (ref) => {
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || !('IntersectionObserver' in window)) return undefined;

    const reveal = (el) => {
      el.removeAttribute('data-reveal-pending');
      el.setAttribute('data-revealed', '');
    };

    const intersection = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal(entry.target);
            intersection.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    const register = (node) => {
      if (!(node instanceof Element)) return;
      const elements = [...node.querySelectorAll(SELECTOR)];
      if (node.matches(SELECTOR)) elements.push(node);
      elements.forEach((el) => {
        // Only hide what the visitor hasn't reached yet.
        if (el.getBoundingClientRect().top > window.innerHeight) {
          el.setAttribute('data-reveal-pending', '');
          intersection.observe(el);
        } else {
          reveal(el);
        }
      });
    };

    register(root);

    const mutations = new MutationObserver((records) => {
      records.forEach((record) => record.addedNodes.forEach(register));
    });
    mutations.observe(root, { childList: true, subtree: true });

    return () => {
      mutations.disconnect();
      intersection.disconnect();
      root.querySelectorAll('[data-reveal-pending]').forEach((el) => el.removeAttribute('data-reveal-pending'));
    };
  }, [ref]);
};

export default useScrollReveal;
